'use client';

import React, { createContext, useState } from 'react';
import { Notice } from '../../components';
import styles from './styles.module.scss';

type ToastItem = (option: { title?: string, message: string, devMessage?: string, persist?: boolean, prompt?: string, onClick?: () => void }) => void;

type ToastContext = {
  toast: {
    info: ToastItem;
    success: ToastItem;
    error: ToastItem;
    warning: ToastItem;
  }
}

const Context = createContext({} as ToastContext);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // eslint-disable-next-line no-spaced-func
  const [toasts, setToasts] = useState<
    {
      id: string,
      message: string,
      devMessage?: string,
      type: 'info' | 'success' | 'error' | 'warning',
      title?: string, persist?: boolean,
      prompt?: string,
      // eslint-disable-next-line func-call-spacing
      onClick?: () => void
      // eslint-disable-next-line indent
    }[]>([]);

  const dismissToast = (id: string) => {
    setToasts((prevToasts) => {
      const newToasts = prevToasts.filter((item) => item.id !== id);
      return newToasts;
    });
  };

  const autoDismissToast = (id: string) => {
    setTimeout(() => {
      dismissToast(id);
    }, 5000 + (parseInt(id, 10) * 1000));
  };

  const toast = {
    error: ({ title, message, devMessage, persist, prompt, onClick }: { title?: string, message?: string, devMessage?: string, persist?: boolean, prompt?: string, onClick?: () => void }) => {
      const id = Math.random().toString();
      setToasts((prevToasts) => [...prevToasts, { devMessage, id, message: message || 'Something went wrong. Try again later', onClick, persist: process.env.NODE_ENV ? true : persist, prompt, title, type: 'error' }]);
      if (!persist && !process.env.NODE_ENV) autoDismissToast(id);
    },
    info: ({ title, message, devMessage, persist, prompt, onClick }: { title?: string, message: string, devMessage?: string, persist?: boolean, prompt?: string, onClick?: () => void }) => {
      const id = Math.random().toString();
      setToasts((prevToasts) => [...prevToasts, { devMessage, id, message, onClick, persist, prompt, title, type: 'info' }]);
      if (!persist) autoDismissToast(id);
    },
    success: ({ title, message, devMessage, persist, prompt, onClick }: { title?: string, message: string, devMessage?: string, persist?: boolean, prompt?: string, onClick?: () => void }) => {
      const id = Math.random().toString();
      setToasts((prevToasts) => [...prevToasts, { devMessage, id, message, onClick, persist, prompt, title, type: 'success' }]);
      if (!persist) autoDismissToast(id);
    },
    warning: ({ title, message, devMessage, persist, prompt, onClick }: { title?: string, message: string, devMessage?: string, persist?: boolean, prompt?: string, onClick?: () => void }) => {
      const id = Math.random().toString();
      setToasts((prevToasts) => [...prevToasts, { devMessage, id, message, onClick, persist, prompt, title, type: 'warning' }]);
      if (!persist) autoDismissToast(id);
    },
  };

  return (
    <Context.Provider value={{ toast }}>
      {children}
      <div className={styles.container}>
        {toasts.map((item, index) => (
          <div
            className={styles.noticeWrapper}
            key={item.id}
          >
            <Notice
              key={index}
              title={item.title}
              message={`${item.devMessage && process.env.NODE_ENV ? `${item.devMessage} :::` : ''} ${item.message}`}
              type={item.type}
              onHidden={() => {
                setToasts((n) => n.filter((_, i) => i !== index));
              }}
              prompt={item.prompt}
              onClick={item.onClick}
            />
          </div>
        ))}
      </div>
    </Context.Provider>
  );
};

export const useToast = () => React.useContext(Context);
